import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState, useMemo, useCallback } from "react";
import { expertPaymentActions } from "_actions";
import ExpertPaymentCheckboxChild from "../../ExpertPaymentCheckboxChild";
import { formatName } from "../ExpertPaymentUtils";
import { ExpertPaymentBuilder } from "../objects/ExpertPaymentBuilder";
import ExpertPaymentHeaderCell from "../../ExpertPaymentHeaderCell";

export default function useExpertListFetch({ search, offset }) {
    const dispatch = useDispatch();
    const [selectedUserIds, setSelectedUserIds] = useState([]);

    useEffect(() => {
        dispatch(expertPaymentActions.listExperts({ search, offset }));
    }, [search, offset]);

    const payments = useSelector((state) => state.expertPayment.expertPayments.data);
    const loading = useSelector((state) => state.expertPayment.expertPayments.loading);
    const expertPaymentList = payments?.experts || [];
    const totalCount = payments?.totalCount || 0;

    const allSelected = useMemo(() => {
        return (
            expertPaymentList.length > 0 &&
            expertPaymentList.every((expert) => selectedUserIds.includes(expert?.user?.id))
        );
    }, [expertPaymentList, selectedUserIds]);

    const someSelected = useMemo(() => {
        return selectedUserIds.length > 0 && !allSelected;
    }, [selectedUserIds, allSelected]);

    const toggleAll = useCallback(() => {
        if (allSelected) {
            setSelectedUserIds([]);
        } else {
            const allIds = expertPaymentList.map((expert) => expert?.user?.id);
            setSelectedUserIds(allIds);
        }
    }, [allSelected, expertPaymentList]);

    const toggleSelection = useCallback((id) => {
        setSelectedUserIds((prev) =>
            prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
        );
    }, []);

    const handleClearState = () => {
        setSelectedUserIds([]);
    }

    const rows = useMemo(() => {
        if (loading || expertPaymentList.length === 0) return [];

        return expertPaymentList.map((expertPayment) => {
            const userId = expertPayment?.user?.id;
            const builder = new ExpertPaymentBuilder();

            builder.setId(userId);
            builder.setName(formatName(expertPayment?.user?.firstName, expertPayment?.user?.lastName));
            builder.setEmail(expertPayment?.user?.email);
            builder.setBalance(expertPayment?.wallet?.balance);
            builder.setLifetimeEarning(expertPayment?.wallet?.lifeTimeEarning);
            builder.setActions(
                <ExpertPaymentCheckboxChild
                    expertPayment={expertPayment}
                    selected={selectedUserIds.includes(userId)}
                    onToggle={() => toggleSelection(userId)}
                />
            );

            return builder.expertPayment;
        });
    }, [expertPaymentList, selectedUserIds, loading, toggleSelection]);

    const columns = useMemo(() => [
        {
            id: "name",
            label: "Name",
            width: 200,
            isSortable: true,
        },
        {
            id: "email",
            label: "Email",
            width: 200,
            isSortable: true,
        },
        {
            id: "balance",
            label: "Balance",
            width: 50,
            isSortable: true,
        },
        {
            id: "lifetimeEarning",
            label: "Lifetime Earning",
            width: 50,
            isSortable: true,
        },
        {
            id: "actions",
            label: (
                <ExpertPaymentHeaderCell
                    selectedUserIds={selectedUserIds}
                    handleClearState={handleClearState}
                    allSelected={allSelected}
                    someSelected={someSelected}
                    onToggleAll={toggleAll}
                />
            ),
            width: 50,
            align: "right",
        },
    ], [allSelected, someSelected, toggleAll, selectedUserIds]);

    return { rows, columns, totalCount, loading, selectedUserIds };
}
